import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { getAppSettings } from "@/lib/api";
import { unscopedKey } from "@/lib/queryClient";

/**
 * Syncs the document's browser-tab branding (title + favicon) with
 * the operator-configured club identity from `/api/settings`.
 *
 * Why this exists: `index.html` ships with `<title>tktic</title>`
 * and `<link rel="icon" href="/favicon.svg">` so the dev/preview
 * shell always has *something*. On prod, the operator uploads a
 * favicon at `/admin/settings`, which the backend serves from
 * `/api/app-assets/favicon`, but nothing in the SPA was rewiring
 * the `<link>` tag — the browser tab kept showing the bundled
 * default. Same story for the page title: every route-specific
 * `useDocumentMeta` call patches the title for that route, but
 * the *baseline* title that shows before any route's hook fires
 * (or on routes that don't call `useDocumentMeta`) was the bundled
 * "tktic".
 *
 * This hook fixes both. It's mounted at the root layout
 * (`PublicAppHeader`) so it runs on every page, fetches the
 * shared `["app-settings"]` cache (also consumed by `/teams`,
 * `GameShell`, `TeamLivePage`), and:
 *
 *   - Sets `document.title` to the configured Club title (or
 *     leaves it alone if the title was already overridden by a
 *     route-level `useDocumentMeta`).
 *   - Updates the `<link rel="icon">` href to the uploaded
 *     favicon URL when one is set.
 *
 * Route-level `useDocumentMeta` calls still take precedence for
 * that route (they overwrite `document.title` on mount + restore
 * the prior value on unmount, which becomes the title this hook
 * set most recently — so navigating back to a route that doesn't
 * override the title naturally inherits the Club title again).
 */
export function useBrandSync(): void {
  const settingsQuery = useQuery({
    queryKey: unscopedKey("app-settings"),
    queryFn: getAppSettings,
    staleTime: 60_000,
  });

  const clubTitle = (settingsQuery.data?.settings?.season_title ?? "").trim();
  const faviconUrl = settingsQuery.data?.assets?.favicon_url ?? "";

  useEffect(() => {
    if (clubTitle) {
      // Always update — route-level hooks restore to the value
      // that was set the moment they mounted, so when this hook's
      // effect re-runs (e.g. after the operator updates the title
      // in /admin/settings and the cache invalidates), the change
      // propagates to every route that hasn't taken over the title
      // for its own purpose.
      document.title = clubTitle;
    }
  }, [clubTitle]);

  useEffect(() => {
    if (!faviconUrl) return;
    // Prefer the <link rel="icon"> already in index.html. If
    // multiple icon links exist (theme variants), update them all
    // so whichever the browser picks reflects the operator's
    // favicon.
    const links = document.head.querySelectorAll<HTMLLinkElement>(
      'link[rel="icon"], link[rel="shortcut icon"]',
    );
    if (links.length === 0) {
      const link = document.createElement("link");
      link.rel = "icon";
      link.href = faviconUrl;
      document.head.appendChild(link);
      return;
    }
    for (const link of links) {
      link.href = faviconUrl;
      // The bundled fallback declares `type="image/svg+xml"`, but
      // an operator-uploaded favicon could be .ico/.png/.svg. Drop
      // the type hint so the browser sniffs from the response
      // content-type (the backend sets the right mime via
      // `/api/app-assets/favicon`).
      link.removeAttribute("type");
    }
  }, [faviconUrl]);
}
